import { db } from './config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { AgendaConfig } from '@/types/models';

const DEFAULT_CONFIG: Omit<AgendaConfig, 'clinicId' | 'updatedAt'> = {
  defaultDuration: 20,
  overbookingLimit: 5,
  fixedSlots: false,
  slotIntervalMin: 20,
  days: {
    0: { enabled: false, start: '08:00', end: '18:00' },
    1: { enabled: true,  start: '08:00', end: '18:00' },
    2: { enabled: true,  start: '08:00', end: '18:00' },
    3: { enabled: true,  start: '08:00', end: '18:00' },
    4: { enabled: true,  start: '08:00', end: '18:00' },
    5: { enabled: true,  start: '08:00', end: '18:00' },
    6: { enabled: false, start: '08:00', end: '13:00' },
  },
};

/** Hidrata um snapshot bruto do Firestore com os defaults — o doc
 *  `clinics/{cid}/config/agenda` historicamente carregou só `blockedDates`
 *  (sem `days`/`defaultDuration`/etc.), e o consumo direto sem merge
 *  fazia campos faltantes crasharem (ex.: `config.days[weekday]` → undefined). */
const hydrate = (raw: Partial<AgendaConfig>, clinicId: string): AgendaConfig => ({
  ...DEFAULT_CONFIG,
  ...raw,
  // `days` precisa de merge profundo — se o doc tem alguns dias mas não todos,
  // não queremos perder os defaults pros dias ausentes.
  days: { ...DEFAULT_CONFIG.days, ...(raw.days ?? {}) },
  clinicId,
  updatedAt: raw.updatedAt ?? Date.now(),
});

export const AgendaConfigService = {
  async getConfig(clinicId: string): Promise<AgendaConfig> {
    const ref = doc(db, 'clinics', clinicId, 'config', 'agenda');
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return hydrate({}, clinicId);
    }
    return hydrate(snap.data() as Partial<AgendaConfig>, clinicId);
  },

  async saveConfig(clinicId: string, config: Omit<AgendaConfig, 'clinicId' | 'updatedAt'>) {
    const ref = doc(db, 'clinics', clinicId, 'config', 'agenda');
    const data: AgendaConfig = { ...config, clinicId, updatedAt: Date.now() };
    // merge: o mesmo doc `config/agenda` também guarda os campos do portal
    // do paciente (blockedDates, minAdvanceHours…) salvos pela aba de
    // Configurações — um setDoc "seco" aqui apagaria tudo isso.
    await setDoc(ref, data, { merge: true });
    return data;
  },
};
